import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { Button } from '../../../ui/button';
import { Calendar } from '../../../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';

export interface DocumentDateInputProps
    extends Omit<
        React.InputHTMLAttributes<HTMLInputElement>,
        'value' | 'onChange' | 'type'
    > {
    value?: string;
    onChange?: (event: { target: { value: string } }) => void;
}

export const DocumentDateInput = React.forwardRef<
    HTMLButtonElement,
    DocumentDateInputProps
>(({ className, value, onChange, ...props }, ref) => {
    const [isOpen, setIsOpen] = React.useState(false);

    // Handle date selection
    const handleSelect = (date: Date | undefined) => {
        if (date && onChange) {
            // Format to YYYY-MM-DD for consistency with input[type="date"]
            const formatted = format(date, 'yyyy-MM-dd');
            onChange({ target: { value: formatted } });
            setIsOpen(false);
        }
    };

    // Parse current value
    // Handle potential undefined or empty string value
    // Fix: Parse YYYY-MM-DD as local date to avoid timezone off-by-one issues
    const selectedDate = React.useMemo(() => {
        if (!value) return undefined;
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
    }, [value]);

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button
                    ref={ref}
                    variant={'outline'}
                    type="button"
                    className={cn(
                        'w-full justify-start text-left font-normal',
                        !value && 'text-muted-foreground',
                        className
                    )}
                >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? (
                        format(selectedDate, 'PPP')
                    ) : (
                        <span>Pick a date</span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleSelect}
                    initialFocus
                />
            </PopoverContent>
        </Popover>
    );
});
DocumentDateInput.displayName = 'DocumentDateInput';
